import React from "react";
import { Box, Heading, Table, Tag, Text, VStack } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import TicketsPerServiceLineGraph from "../charts/TicketsPerServiceLineGraph";
import TicketsPerServicePieGraph from "../charts/TicketsPerServicePieGraph";
import ReportDataTable from "../../reports/ReportDataTable";
import {
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../../../store/renderConfig";
import { formatNumber } from "../../../store/helpers";
import { getTicketsPerServiceRowsForScope } from "../../../store/ticketsPerServiceExport";

const PercentMeta = ({ value }) => (
  <Text color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="uppercase">
    ({Number(value || 0).toFixed(2)}%)
  </Text>
);

export const renderTicketsPerServiceCell = (serviceData, columnId) => {
  if (columnId === "serviceName") return serviceData.serviceName || serviceData.name;
  if (columnId === "total") return <Text color="var(--blue)">{formatNumber(serviceData.total)}</Text>;
  if (columnId === "average") return formatNumber(serviceData.average);
  if (columnId === "min") return formatNumber(serviceData.min);
  if (columnId === "max") return formatNumber(serviceData.max);
  if (columnId === "completed") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--green)">{formatNumber(serviceData.completed)}</Text>
        <PercentMeta value={serviceData.pctCompleted} />
      </VStack>
    );
  }
  if (columnId === "noShow") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--red)">{formatNumber(serviceData.noShow)}</Text>
        <PercentMeta value={serviceData.pctNoShow} />
      </VStack>
    );
  }
  if (columnId === "unattended") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--red)">{formatNumber(serviceData.unattended)}</Text>
        <PercentMeta value={serviceData.pctUnattended} />
      </VStack>
    );
  }
  return null;
};

const ChartedServiceTable = ({ serviceData, index, branchId, branchName, chartTitle }) => (
  <Box key={index} className="reportContainer ticketsPerServiceBlock" mt="20px">
    <Box mb="20px" mt="30px">
      <Heading fontSize="24px" color="#222" fontWeight="600">
        {index + 1}. {serviceData.name}
      </Heading>
      {branchId ? (
        <Tag.Root>
          <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
        </Tag.Root>
      ) : null}
    </Box>
    <Table.Root className="stripedTable" size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader borderRight="1px solid #dadce0">Total Tickets</Table.ColumnHeader>
          <Table.ColumnHeader>Avg /day.</Table.ColumnHeader>
          <Table.ColumnHeader>Min.</Table.ColumnHeader>
          <Table.ColumnHeader borderRight="1px solid #dadce0">Max</Table.ColumnHeader>
          <Table.ColumnHeader>Completed</Table.ColumnHeader>
          <Table.ColumnHeader>No Show</Table.ColumnHeader>
          <Table.ColumnHeader>Unattended</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        <Table.Row>
          <Table.Cell borderRight="1px solid #dadce0">
            <Text color="var(--blue)">{formatNumber(serviceData.total)}</Text>
          </Table.Cell>
          <Table.Cell>{formatNumber(serviceData.averageTicketsPerDay)}</Table.Cell>
          <Table.Cell>{formatNumber(serviceData.minTicketsPerDay)}</Table.Cell>
          <Table.Cell borderRight="1px solid #dadce0">
            {formatNumber(serviceData.maxTicketsPerDay)}
          </Table.Cell>
          <Table.Cell>{renderTicketsPerServiceCell(serviceData, "completed")}</Table.Cell>
          <Table.Cell>{renderTicketsPerServiceCell(serviceData, "noShow")}</Table.Cell>
          <Table.Cell>{renderTicketsPerServiceCell(serviceData, "unattended")}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table.Root>
    <Box className="ticketServiceChartGrid">
      <Box className="ticketServiceChartPane ticketServiceChartPane--pie">
        <TicketsPerServicePieGraph
          title="Ticket Management Comparison"
          chart={serviceData}
          chartHeight="200px"
        />
      </Box>
      <Box className="ticketServiceChartPane ticketServiceChartPane--line">
        <TicketsPerServiceLineGraph
          title={`${serviceData.name}: ${chartTitle} Day by Day Comparison`}
          chart={serviceData.days}
          chartHeight="200px"
        />
      </Box>
    </Box>
  </Box>
);

const TicketsPerServiceTable = ({
  branchId = null,
  data,
  branchIndex,
  branchName,
  numServices,
  renderConfig,
  onRenderConfigChange,
}) => {
  const { showEmptyDataSets } = useSelector((state) => state.defaultReducer);
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const chartTitle = branchId ? `${branchName} Branch` : "Entire Organization,";
  const branchTitle = branchId
    ? ` ${branchIndex ? `${branchIndex}. ` : ""}${branchName} Branch`
    : "Entire Organization";
  const scope = branchId ? "Branch" : "Organization";
  const rows = getTicketsPerServiceRowsForScope({ data, branchName, scope }, config);
  const visibleChartRows = (data || []).filter(
    (item) => showEmptyDataSets || (!showEmptyDataSets && Number(item.total) > 0)
  );

  return (
    <Box mt={10}>
      <Box mb="20px">
        <Heading fontSize="30px" color="#222" fontWeight={branchId ? "600" : "700"}>
          {branchTitle}
        </Heading>
        {branchId ? <p>{numServices} services.</p> : null}
      </Box>

      {!config.charts.enabled ? (
        <Box className="ticketsPerServiceBlock">
          <ReportDataTable
            columns={TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
            rows={rows}
            renderConfig={config}
            renderCell={renderTicketsPerServiceCell}
            presortedRows={true}
            onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
          />
        </Box>
      ) : null}

      {config.charts.enabled
        ? visibleChartRows.map((serviceData, index) => (
            <ChartedServiceTable
              key={index}
              serviceData={serviceData}
              index={index}
              branchId={branchId}
              branchName={branchName}
              chartTitle={chartTitle}
            />
          ))
        : null}
      {!rows.length && !config.charts.enabled ? (
        <Text fontSize="sm">No data available for this report</Text>
      ) : null}
    </Box>
  );
};

export default TicketsPerServiceTable;
